import { useQuery } from '@tanstack/react-query';
import { fetchDMs, fetchMySpaces } from '../../lib/api';

// First-run DETECTION (kept tiny — the main entry bundle imports `useFirstRun` to
// decide whether to LAZY-load the Concierge Thread). The first-run CONTENT (Scout
// greeting, chips, identity helpers) lives in scoutContent.ts so it ships in the
// lazy chunk, never the entry bundle.
//
// "Concierge Thread" (docs/ux-mocks/first-run/): a brand-new, EMPTY account that opens
// Messages finds the familiar two-pane chat shell pre-seeded with one waiting Scout
// thread — never two empty lists. (It is no longer the LANDING: T-0162 sends a bare "/"
// to the canvas, and T-0175 sends signup straight there too. This is what ✉ Messages
// shows a first-run account, one tap from the canvas.)

/**
 * PURE: a fresh account = no spaces AND no DMs. The signal that the user has
 * nothing yet and should meet Scout instead of an empty console. Tested without
 * React Query.
 */
export function isAccountEmpty(spaceCount: number, dmCount: number): boolean {
  return spaceCount === 0 && dmCount === 0;
}

export interface FirstRunState {
  /** true once BOTH lists loaded successfully AND both are empty. */
  isFirstRun: boolean;
  /** true once both queries have settled (so the shell doesn't flash). */
  resolved: boolean;
}

/**
 * First-run decision, reusing the SAME query keys the sidebar lists already use
 * (`['me','spaces']`, `['dms']`) so there's no extra fetch. Only commits to
 * "first run" once both have SUCCEEDED — an in-flight or degraded account never
 * briefly shows the concierge over real data.
 *
 * `resolved` counts a SETTLED-WITH-ERROR query as resolved: the shell renders
 * `Loading…` until `resolved`, so requiring success here wedged the whole
 * console on the spinner whenever either endpoint failed (staging without
 * `/api/me/dms` was the live case — though a missing endpoint now settles
 * successfully as empty, see `fetchDMs`; this keeps a 500 / network failure
 * from re-creating the hang). On error the account is treated as NOT first-run:
 * the normal console renders and each list surfaces its own empty/error state.
 *
 * `isFetched` (settled at least once — success or error) rather than
 * `isSuccess || isError`, because it is MONOTONIC per cached query: when a
 * later observer of the same key mounts (DMsList inside the console body),
 * React Query refetches the errored query and flips its status back to
 * `pending` — a momentary `isError`-based gate would un-resolve, unmount that
 * very observer, and oscillate spinner↔console forever. Once both lists have
 * answered once, the boot decision is made and never reverts.
 */
export function useFirstRun(): FirstRunState {
  const spaces = useQuery({
    queryKey: ['me', 'spaces'],
    queryFn: fetchMySpaces,
  });
  const dms = useQuery({ queryKey: ['dms'], queryFn: fetchDMs });
  const resolved = spaces.isFetched && dms.isFetched;
  const isFirstRun =
    spaces.isSuccess &&
    dms.isSuccess &&
    isAccountEmpty(spaces.data?.length ?? 0, dms.data?.dms.length ?? 0);
  return { isFirstRun, resolved };
}
